import React from 'react';

interface QuantitySelectorProps {
  quantity: number;
  onChange: (quantity: number) => void;
  min?: number;
  max?: number;
  label?: string;
}

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  quantity,
  onChange,
  min = 1,
  max = 99,
  label = 'Quantity',
}) => {
  const safeMin = typeof min === 'number' && !Number.isNaN(min) ? min : 1;
  const safeMax = typeof max === 'number' && !Number.isNaN(max) && max >= safeMin ? max : Math.max(99, safeMin);
  const safeQty = typeof quantity === 'number' && !Number.isNaN(quantity) ? Math.max(0, quantity) : safeMin;

  const handleDecrease = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = Math.max(safeMin, safeQty - 1);
    if (Number.isInteger(next)) onChange(next);
  };

  const handleIncrease = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = Math.min(safeMax, safeQty + 1);
    if (Number.isInteger(next)) onChange(next);
  };

  return (
    <div className="flex border border-stone-200" role="group" aria-label={label}>
      <button
        type="button"
        onClick={handleDecrease}
        disabled={safeQty <= safeMin}
        className="px-5 py-3 text-stone-400 hover:text-stone-900 font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="Decrease quantity"
      >
        −
      </button>
      <span
        className="px-6 py-3 font-bold border-x border-stone-200 min-w-[3rem] text-center tabular-nums"
        aria-live="polite"
        aria-atomic="true"
      >
        {safeQty}
      </span>
      <button
        type="button"
        onClick={handleIncrease}
        disabled={safeQty >= safeMax}
        className="px-5 py-3 text-stone-400 hover:text-stone-900 font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
};

export default QuantitySelector;
